/**
 * Hub "Mon équipe" (espace compétition)
 *
 * Layout colonne gauche (MasterDetail, comme la page "Tables de jeu") :
 * en-tête équipe + navigation Agenda / Composition / Mercato / Chat / Forum.
 * Le chat et le forum de l'équipe sont affichés directement dans cette page
 * (composants réutilisés depuis les pages Discussions / Forums), sans
 * naviguer ailleurs — pratique pour rebasculer facilement vers une autre
 * rubrique (ex. le mercato).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import MasterDetail from '../../components/MasterDetail';
import TeamCompositionView from './TeamCompositionView';
import TeamMercatoView from './TeamMercatoView';
import TeamAgendaView from './TeamAgendaView';
import TeamChatPanel from '../../components/competitions/TeamChatPanel';
import { DivisionForumPanel } from '../../components/competitions/DivisionForumPanel';
import { CalendarIcon, GroupIcon, PersonAddIcon, ForumsIcon, ChatIcon } from '../../components/Icons';
import { getMercato, type MyTeam } from '../../lib/teamsApi';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';

type HubView = 'agenda' | 'composition' | 'mercato' | 'chat' | 'forum';

interface TeamHubPageProps {
  session: Session;
  team: MyTeam;
  onChanged: () => void;
  onLeft: () => void;
}

function fmtDeadline(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatUnreadBadge(n: number): string {
  return n > 99 ? '99+' : String(n);
}

export default function TeamHubPage({ session, team, onChanged, onLeft }: TeamHubPageProps) {
  const [view, setView] = useState<HubView>('agenda');
  const [hasSelection, setHasSelection] = useState(false);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [forumUnread, setForumUnread] = useState(0);
  const [teamConversationId, setTeamConversationId] = useState<string | null>(null);
  const viewRef = useRef<HubView>(view);
  viewRef.current = view;

  useEffect(() => {
    if (!team.is_staff) return;
    getMercato(session.token!, team.id, 'attente')
      .then((d) => setPendingApplications(d.items.filter((m) => m.initiative === 'joueur').length))
      .catch(() => setPendingApplications(0));
  }, [session.token, team.id, team.is_staff]);

  // Compteurs non-lus chat équipe + forum équipe (même sources que Discussions / Forums)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [groupRes, extraRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/conversations/team-group?pseudo=${encodeURIComponent(session.pseudo)}`),
          fetch(`${BACKEND_URL}/api/forums/extra?pseudo=${encodeURIComponent(session.pseudo)}`),
        ]);
        if (cancelled) return;
        if (groupRes.ok) {
          const data = await groupRes.json() as {
            teamGroup?: { id: string; unreadCount?: number } | null;
          };
          if (data.teamGroup) {
            setTeamConversationId(data.teamGroup.id);
            setChatUnread(typeof data.teamGroup.unreadCount === 'number' ? data.teamGroup.unreadCount : 0);
          }
        }
        if (extraRes.ok) {
          const data = await extraRes.json() as {
            teamForum?: { unreadCount?: number } | null;
          };
          setForumUnread(
            typeof data.teamForum?.unreadCount === 'number' ? data.teamForum.unreadCount : 0,
          );
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [session.pseudo, team.id]);

  // Incrémente la bulle chat si un message arrive alors que le panneau n'est pas ouvert
  useEffect(() => {
    if (!teamConversationId) return;
    const socket: Socket = io(BACKEND_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('join_global', { pseudo: session.pseudo, compet: session.compet });
    });
    const onMessage = (message: { conversationId?: string; author?: string }) => {
      if (message.conversationId !== teamConversationId) return;
      if ((message.author || '').toLowerCase() === session.pseudo.toLowerCase()) return;
      if (viewRef.current === 'chat') return;
      setChatUnread((n) => n + 1);
    };
    socket.on('direct_message', onMessage);
    return () => {
      socket.off('direct_message', onMessage);
      socket.disconnect();
    };
  }, [teamConversationId, session.pseudo, session.compet]);

  const refreshForumUnread = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forums/pulse?space=competition`, {
        headers: { 'X-User-Pseudo': session.pseudo },
      });
      if (!res.ok) return;
      const data = await res.json() as { teamUnreadCount?: number | null };
      if (typeof data.teamUnreadCount === 'number') setForumUnread(data.teamUnreadCount);
    } catch {
      /* ignore */
    }
  }, [session.pseudo]);

  const select = (v: HubView) => {
    if (view === 'forum' && v !== 'forum') {
      void refreshForumUnread();
    }
    if (v === 'chat') setChatUnread(0);
    setView(v);
    setHasSelection(true);
  };

  const VIEW_LABEL: Record<HubView, string> = {
    agenda: 'Agenda', composition: 'Composition', mercato: 'Mercato',
    chat: "Chat de l'équipe", forum: "Forum de l'équipe",
  };

  const master = (
    <>
      <div className="tm-sidebar-team">
        <PlayerAvatar
          pseudo={team.nom}
          size={48}
          photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(team.nom)}?size=96`}
        />
        <div className="tm-sidebar-team-info">
          <span className="tm-sidebar-team-name">{team.nom}</span>
          <span className="tm-sidebar-team-meta">
            {team.members.length}/8 · {team.is_captain ? 'Capitaine' : team.is_lieutenant ? 'Lieutenant' : 'Membre'}
          </span>
        </div>
      </div>

      {team.next_deadline && (
        <div className="tm-nav-deadline">
          Prochaine échéance : <strong>{fmtDeadline(team.next_deadline.rdv)}</strong>
        </div>
      )}

      <nav className="tm-nav">
        <button className={`tm-nav-item ${view === 'agenda' ? 'active' : ''}`} onClick={() => select('agenda')}>
          <span className="tm-nav-item-icon"><CalendarIcon size={17} /></span>
          <span className="tm-nav-item-label">Agenda</span>
        </button>
        <button className={`tm-nav-item ${view === 'composition' ? 'active' : ''}`} onClick={() => select('composition')}>
          <span className="tm-nav-item-icon"><GroupIcon size={17} /></span>
          <span className="tm-nav-item-label">Composition</span>
        </button>
        <button className={`tm-nav-item ${view === 'mercato' ? 'active' : ''}`} onClick={() => select('mercato')}>
          <span className="tm-nav-item-icon"><PersonAddIcon size={17} /></span>
          <span className="tm-nav-item-label">Mercato</span>
          {pendingApplications > 0 && <span className="tm-nav-item-badge">{pendingApplications}</span>}
        </button>
        <button className={`tm-nav-item ${view === 'chat' ? 'active' : ''}`} onClick={() => select('chat')}>
          <span className="tm-nav-item-icon"><ChatIcon size={17} /></span>
          <span className="tm-nav-item-label">Chat de l'équipe</span>
          {chatUnread > 0 && (
            <span className="tm-nav-item-badge tm-nav-item-badge--unread" title={`${chatUnread} non lu${chatUnread > 1 ? 's' : ''}`}>
              {formatUnreadBadge(chatUnread)}
            </span>
          )}
        </button>
        <button className={`tm-nav-item ${view === 'forum' ? 'active' : ''}`} onClick={() => select('forum')}>
          <span className="tm-nav-item-icon"><ForumsIcon size={17} /></span>
          <span className="tm-nav-item-label">Forum de l'équipe</span>
          {forumUnread > 0 && (
            <span className="tm-nav-item-badge tm-nav-item-badge--unread" title={`${forumUnread} non lu${forumUnread > 1 ? 's' : ''}`}>
              {formatUnreadBadge(forumUnread)}
            </span>
          )}
        </button>
      </nav>
    </>
  );

  const detail = (
    <>
      {view === 'agenda' && (
        <TeamAgendaView session={session} team={team} />
      )}
      {view === 'composition' && (
        <TeamCompositionView session={session} team={team} onChanged={onChanged} onLeft={onLeft} />
      )}
      {view === 'mercato' && (
        <TeamMercatoView session={session} team={team} />
      )}
      {view === 'chat' && (
        <div className="tm-full-panel">
          <TeamChatPanel session={session} onMarkedRead={() => setChatUnread(0)} />
        </div>
      )}
      {view === 'forum' && (
        <div className="tm-full-panel">
          <DivisionForumPanel
            teamId={team.id}
            viewerPseudo={session.pseudo}
            allowCompose
            onUnreadCleared={(cleared) =>
              setForumUnread((n) => Math.max(0, n - cleared))
            }
          />
        </div>
      )}
    </>
  );

  return (
    <div className="tm-hub">
      <MasterDetail
        master={master}
        detail={detail}
        detailTitle={VIEW_LABEL[view]}
        hasSelection={hasSelection}
        onBack={() => setHasSelection(false)}
      />
    </div>
  );
}
